import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { OrderComparisonCard } from "./OrderComparisonCard";
import { MergeOrdersDialog } from "@/components/orders/MergeOrdersDialog";
import { useDuplicateOrderPairs } from "@/hooks/useDuplicateOrderPairs";
import { useDismissDuplicatePair } from "@/hooks/useMergeOrders";
import { CheckCircle2 } from "lucide-react";

export function DuplicateOrdersReview() {
  const { data: pairs = [], isLoading } = useDuplicateOrderPairs();
  const dismiss = useDismissDuplicatePair();
  const [index, setIndex] = useState(0);
  const [mergeOpen, setMergeOpen] = useState(false);

  useEffect(() => {
    if (index >= pairs.length) setIndex(0);
  }, [pairs.length, index]);

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Loading duplicates…</p>;
  }

  if (pairs.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-10 text-center min-h-[160px]">
        <CheckCircle2 className="h-10 w-10 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">No duplicate orders to review</p>
      </div>
    );
  }

  const total = pairs.length;
  const current = pairs[Math.min(index, total - 1)];
  // Left = API-created (keeper by default), Right = ATMS (merged into keeper).
  const keeper = current.a;
  const merged = current.b;

  const goNext = () => setIndex((i) => (i + 1) % total);
  const goPrev = () => setIndex((i) => (i - 1 + total) % total);

  const handleDismiss = async () => {
    await dismiss.mutateAsync({ orderAId: current.a.id, orderBId: current.b.id });
    // After dismissal, the list refetches; index stays and lands on the next pair.
  };

  return (
    <div className="flex flex-col gap-4 pb-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm text-muted-foreground">
            We found orders that may be duplicates. Please review and merge any duplicate orders.
          </p>
        </div>
      </div>

      <div className="rounded-lg border bg-muted/40">
        <div className="flex items-center justify-between gap-3 border-b bg-card/60 px-4 py-3 rounded-t-lg">
          <div className="text-base font-medium whitespace-nowrap">
            Are these two orders the same?
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              Comparison {Math.min(index + 1, total)} of {total}
            </span>
            {index > 0 && (
              <Button variant="ghost" size="sm" onClick={goPrev}>
                ← Previous
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={goNext} disabled={total <= 1}>
              Next →
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 bg-muted/60 p-4">
          <OrderComparisonCard order={keeper} />
          <OrderComparisonCard order={merged} />
        </div>

        <div className="flex justify-end gap-2 border-t bg-card/60 px-4 py-3 rounded-b-lg">
          <Button
            variant="outline"
            onClick={handleDismiss}
            disabled={dismiss.isPending}
          >
            No, keep separate
          </Button>
          <Button onClick={() => setMergeOpen(true)}>
            Yes, merge orders
          </Button>
        </div>
      </div>

      <MergeOrdersDialog
        open={mergeOpen}
        onOpenChange={setMergeOpen}
        keeper={keeper}
        merged={merged}
        onMerged={goNext}
      />
    </div>
  );
}